import { Fragment, useContext, useState } from "react";

import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { TransitionGroup } from "react-transition-group";

import { css } from "@emotion/react";

import { BannerType } from "@dotnet/telematics-design-system";

import DropzoneContext from "context/dropzone/dropzoneContext";
import { useFileSize } from "shared/hooks/useFileSize";
import { FILE_UPLOAD_STATUS } from "shared/types/enums";

import Frame from "../Frame";
import { MultipleFilesProps } from "../types";
import UploadedFileMultiple from "./UploadedFileMultiple";

const MultipleFiles = ({
    supportedFormats,
    maxFiles,
    isDisabled,
    maxFileNameLength,
    isLoading,
    isUploaded,
    maxSizeBytes
}: MultipleFilesProps) => {
    const { t } = useTranslation();

    const { multipleFiles, setFiles, setMessage } = useContext(DropzoneContext);

    const { renderFileSize } = useFileSize();

    const [isDragged, setIsDragged] = useState(false);

    const isMaxFileLimitReached = multipleFiles?.length >= maxFiles;

    const isDisabledDropzone = isDisabled || isMaxFileLimitReached || isLoading;

    const { getRootProps, getInputProps } = useDropzone({
        accept: { "application/x-file": supportedFormats },
        disabled: isDisabledDropzone,
        maxSize: maxSizeBytes,
        onDragEnter: () => setIsDragged(true),
        onDragLeave: () => setIsDragged(false),
        onDrop: (acceptedFiles, rejectedFiles) => {
            if (acceptedFiles.length > 0) {
                const sliceEnd = maxFiles - multipleFiles.length;

                const filesWithCorrectNameLength = acceptedFiles.filter(
                    ({ name }) =>
                        maxFileNameLength
                            ? name.length <= maxFileNameLength
                            : true
                );

                const filesToUpload = filesWithCorrectNameLength.slice(
                    0,
                    sliceEnd
                );

                const potentialAmountOfFiles =
                    multipleFiles.length + filesWithCorrectNameLength.length;

                const tooManyFiles = potentialAmountOfFiles > maxFiles;

                setFiles(filesToUpload);

                tooManyFiles &&
                    setMessage({
                        status: BannerType.Warning,
                        title: t("Dropzone##too many files title", {
                            maxFiles: maxFiles
                        }),
                        description: t("Dropzone##too many files description")
                    });

                filesWithCorrectNameLength.length < acceptedFiles.length &&
                    setMessage(
                        {
                            status: BannerType.Error,
                            title: t("Dropzone##too long file name", {
                                maxLength: maxFileNameLength
                            })
                        },
                        true
                    );
            }

            if (rejectedFiles.length > 0) {
                const message =
                    maxSizeBytes !== undefined
                        ? t("Dropzone##invalid format or size", {
                              size: renderFileSize(maxSizeBytes)
                          })
                        : t("Dropzone##file invalid type");

                setMessage({
                    status: BannerType.Error,
                    title: message
                });
            }

            setIsDragged(false);
        }
    });

    const sortedByErrorsFirst = multipleFiles
        ?.map(file => ({ ...file }))
        .sort((firstFile, secondFile) => {
            const firstFileError =
                firstFile.uploadStatus === FILE_UPLOAD_STATUS.Failed;

            const secondFileError =
                secondFile.uploadStatus === FILE_UPLOAD_STATUS.Failed;

            if (firstFileError && !secondFileError) {
                return -1;
            }

            if (secondFileError && !firstFileError) {
                return 1;
            }

            return 0;
        });

    return (
        <Fragment>
            <Frame
                getRootProps={getRootProps}
                getInputProps={getInputProps}
                isDragged={isDragged}
                supportedFormats={supportedFormats}
                isMultiple
                maxSizeBytes={maxSizeBytes}
                isDisabled={isDisabledDropzone}
                isUploaded={isUploaded}
                tooltipText={
                    isMaxFileLimitReached
                        ? t("Dropzone##maximum file limit reached")
                        : ""
                }
            />

            <TransitionGroup component={null}>
                {sortedByErrorsFirst?.map(
                    ({ file, id, uploadStatus, errorMessage }) => (
                        <UploadedFileMultiple
                            key={id}
                            name={file.name}
                            id={id}
                            size={file.size}
                            isLoading={isLoading}
                            failedUpload={errorMessage}
                            fileUploadStatus={uploadStatus}
                            css={css({
                                marginTop: "8px"
                            })}
                        />
                    )
                )}
            </TransitionGroup>
        </Fragment>
    );
};

export default MultipleFiles;
